/**
 * AYU Gamification — Konfiguration
 * Prana-Punkte, AYU-Pfad (7 Level), 15+ Abzeichen,
 * Wöchentliche Challenges, AYU Score
 *
 * Basis: Level 1-4, 8 Abzeichen, 1 Challenge/Woche
 * Premium: Alle 7 Level, 15+ Abzeichen, 3/Woche
 */

// Prana-Punkte: Werte pro Aktivität
export const PRANA_POINTS = {
  tagebuch_eintrag: 10,
  coach_session: 15,
  tagesroutine_abgeschlossen: 25,
  meditation_session: 20,
  rezept_gekocht: 15,
  ernaehrungsprofil_aktualisiert: 10,
  community_beitrag: 5,
  dokument_hochgeladen: 20,
  mikrobiom_hochgeladen: 50,
  kurs_modul_abgeschlossen: 100,
  kurs_woche_abgeschlossen: 250,
  kurs_komplett_abgeschlossen: 1000,
  challenge_abgeschlossen: 75,
  challenge_woche_komplett: 200,
  streak_7_tage: 50,
  streak_30_tage: 200,
  streak_100_tage: 500,
  erstes_abzeichen: 25,
  level_aufstieg: 100,
  login_bonus: 2,
  profil_vollstaendig: 30,
  voice_chat_genutzt: 15,
  guided_session_gehoert: 20,
} as const;

export type PranaActivityType = keyof typeof PRANA_POINTS;

// AYU-Pfad: 7 Level (Wachstums-Metapher)
export interface AyuLevel {
  id: number;
  name: string;
  nameSanskrit: string;
  description: string;
  requiredPoints: number;
  premiumOnly: boolean;
  unlockReward: string;
  color: string;
}

export const AYU_LEVELS: AyuLevel[] = [
  {
    id: 1, name: "Samen", nameSanskrit: "Bija",
    description: "Du hast den ersten Schritt getan. Der Samen deiner Gesundheitsreise ist gepflanzt.",
    requiredPoints: 0, premiumOnly: false,
    unlockReward: "Samen-Avatar", color: "#8B7355",
  },
  {
    id: 2, name: "Keimling", nameSanskrit: "Ankura",
    description: "Dein Bewusstsein waechst. Die ersten Wurzeln deiner neuen Gewohnheiten greifen.",
    requiredPoints: 200, premiumOnly: false,
    unlockReward: "Keimling-Avatar + Tagesspruch", color: "#7D9B5C",
  },
  {
    id: 3, name: "Spross", nameSanskrit: "Praroh",
    description: "Du waechst stetig. Deine Routinen werden zur zweiten Natur.",
    requiredPoints: 600, premiumOnly: false,
    unlockReward: "Spross-Avatar + Woechentlicher Insight", color: "#6B8E4E",
  },
  {
    id: 4, name: "Pflanze", nameSanskrit: "Vriksha",
    description: "Du stehst fest verwurzelt. Dein Koerper und Geist finden Balance.",
    requiredPoints: 1500, premiumOnly: false,
    unlockReward: "Pflanze-Avatar + Monatsrueckblick", color: "#4A7C3F",
  },
  {
    id: 5, name: "Bluete", nameSanskrit: "Pushpa",
    description: "Du erbuehst. Dein Wissen und deine Praxis tragen erste Fruechte.",
    requiredPoints: 3500, premiumOnly: true,
    unlockReward: "Bluete-Avatar + Persoenlicher Kraeuterplan", color: "#C9A96E",
  },
  {
    id: 6, name: "Baum", nameSanskrit: "Maha Vriksha",
    description: "Du bist stark und bestaendig. Andere koennen sich an dir orientieren.",
    requiredPoints: 7000, premiumOnly: true,
    unlockReward: "Baum-Avatar + Community-Mentor-Status", color: "#2D6A1E",
  },
  {
    id: 7, name: "Hain", nameSanskrit: "Vana",
    description: "Du bist ein lebendiger Garten der Gesundheit. Dein Wissen naehrt auch andere.",
    requiredPoints: 15000, premiumOnly: true,
    unlockReward: "Hain-Avatar + Exklusiver Zugang", color: "#1A4A12",
  },
];

// Abzeichen-Typen
export type BadgeCategory =
  | "tagebuch" | "meditation" | "ernaehrung" | "schlaf"
  | "routine" | "coach" | "kurse" | "community"
  | "dokumente" | "allgemein";

export interface BadgeCondition {
  type: "count" | "streak" | "score" | "special";
  activity?: string;
  threshold: number;
}

export interface Badge {
  id: string;
  name: string;
  nameSanskrit: string;
  description: string;
  icon: string;
  category: BadgeCategory;
  condition: BadgeCondition;
  premiumOnly: boolean;
  bonusPoints: number;
}

export const BADGES: Badge[] = [
  // Basis-Abzeichen (8)
  { id: "erste_schritte", name: "Erste Schritte", nameSanskrit: "Pratham",
    description: "Schliesse dein Onboarding ab und entdecke deinen Phaenotyp.",
    icon: "seedling", category: "allgemein",
    condition: { type: "special", activity: "onboarding_complete", threshold: 1 },
    premiumOnly: false, bonusPoints: 25 },
  { id: "tagebuch_anfaenger", name: "Gedankenspiegel", nameSanskrit: "Manas Darpana",
    description: "Schreibe deinen ersten Tagebuch-Eintrag.",
    icon: "book-open", category: "tagebuch",
    condition: { type: "count", activity: "tagebuch_eintrag", threshold: 1 },
    premiumOnly: false, bonusPoints: 15 },
  { id: "tagebuch_meister", name: "Innerer Chronist", nameSanskrit: "Katha Lekhak",
    description: "Schreibe 30 Tagebuch-Eintraege.",
    icon: "library", category: "tagebuch",
    condition: { type: "count", activity: "tagebuch_eintrag", threshold: 30 },
    premiumOnly: false, bonusPoints: 100 },
  { id: "coach_entdecker", name: "Weisheitssucher", nameSanskrit: "Jnana Arthi",
    description: "Fuehre deine erste Coach-Sitzung.",
    icon: "message-circle", category: "coach",
    condition: { type: "count", activity: "coach_session", threshold: 1 },
    premiumOnly: false, bonusPoints: 15 },
  { id: "coach_vertraut", name: "Vertrauter Pfad", nameSanskrit: "Vishwas Marg",
    description: "Fuehre 20 Coach-Sitzungen.",
    icon: "brain", category: "coach",
    condition: { type: "count", activity: "coach_session", threshold: 20 },
    premiumOnly: false, bonusPoints: 75 },
  { id: "routine_starter", name: "Morgenrituale", nameSanskrit: "Pratah Kriya",
    description: "Schliesse deine erste Tagesroutine ab.",
    icon: "sunrise", category: "routine",
    condition: { type: "count", activity: "tagesroutine_abgeschlossen", threshold: 1 },
    premiumOnly: false, bonusPoints: 15 },
  { id: "streak_woche", name: "Sieben Sonnen", nameSanskrit: "Sapta Surya",
    description: "Halte eine 7-Tage-Serie.",
    icon: "flame", category: "allgemein",
    condition: { type: "streak", activity: "gesamt", threshold: 7 },
    premiumOnly: false, bonusPoints: 50 },
  { id: "community_willkommen", name: "Sangha-Mitglied", nameSanskrit: "Sangha Sadasya",
    description: "Schreibe deinen ersten Community-Beitrag.",
    icon: "users", category: "community",
    condition: { type: "count", activity: "community_beitrag", threshold: 1 },
    premiumOnly: false, bonusPoints: 10 },

  // Premium-Abzeichen (7+)
  { id: "meditation_stille", name: "Stille Quelle", nameSanskrit: "Shanti Srotas",
    description: "Hoere 10 Meditations-Sessions.",
    icon: "headphones", category: "meditation",
    condition: { type: "count", activity: "meditation_session", threshold: 10 },
    premiumOnly: true, bonusPoints: 75 },
  { id: "meditation_meister", name: "Lotussitz", nameSanskrit: "Padmasana",
    description: "Hoere 50 Meditations-Sessions.",
    icon: "flower-2", category: "meditation",
    condition: { type: "count", activity: "meditation_session", threshold: 50 },
    premiumOnly: true, bonusPoints: 200 },
  { id: "ernaehrung_weise", name: "Kuechenweise", nameSanskrit: "Paka Vidya",
    description: "Koche 10 personalisierte Rezepte.",
    icon: "chef-hat", category: "ernaehrung",
    condition: { type: "count", activity: "rezept_gekocht", threshold: 10 },
    premiumOnly: true, bonusPoints: 75 },
  { id: "dinacharya_meister", name: "Dinacharya-Meister", nameSanskrit: "Dinacharya Siddha",
    description: "Schliesse 30 Tage lang deine Tagesroutine ab.",
    icon: "sun", category: "routine",
    condition: { type: "count", activity: "tagesroutine_abgeschlossen", threshold: 30 },
    premiumOnly: true, bonusPoints: 150 },
  { id: "streak_monat", name: "Mondzyklen", nameSanskrit: "Chandra Chakra",
    description: "Halte eine 30-Tage-Serie.",
    icon: "moon", category: "allgemein",
    condition: { type: "streak", activity: "gesamt", threshold: 30 },
    premiumOnly: true, bonusPoints: 200 },
  { id: "streak_100", name: "Hundert Sonnengruesse", nameSanskrit: "Shata Surya Namaskar",
    description: "Halte eine 100-Tage-Serie.",
    icon: "gem", category: "allgemein",
    condition: { type: "streak", activity: "gesamt", threshold: 100 },
    premiumOnly: true, bonusPoints: 500 },
  { id: "dokument_forscher", name: "Gesundheitsforscher", nameSanskrit: "Svasthya Anusandhata",
    description: "Lade 3 Gesundheitsdokumente hoch.",
    icon: "microscope", category: "dokumente",
    condition: { type: "count", activity: "dokument_hochgeladen", threshold: 3 },
    premiumOnly: true, bonusPoints: 50 },
  { id: "ayu_score_90", name: "Strahlende Gesundheit", nameSanskrit: "Prakash Svasthya",
    description: "Erreiche einen AYU Score von 90 oder hoeher.",
    icon: "sparkles", category: "allgemein",
    condition: { type: "score", activity: "ayu_score", threshold: 90 },
    premiumOnly: true, bonusPoints: 300 },
];

// Challenge-Templates
export interface ChallengeTemplate {
  id: string;
  title: string;
  description: string;
  category: BadgeCategory;
  targetCount: number;
  activityType: string;
  rewardPoints: number;
  premiumOnly: boolean;
  season?: "fruehling" | "sommer" | "herbst" | "winter";
}

export const CHALLENGE_TEMPLATES: ChallengeTemplate[] = [
  // Basis-Challenges (1/Woche)
  { id: "tagebuch_3x", title: "Gedanken festhalten",
    description: "Schreibe diese Woche 3 Tagebuch-Eintraege.",
    category: "tagebuch", targetCount: 3, activityType: "tagebuch_eintrag",
    rewardPoints: 75, premiumOnly: false },
  { id: "coach_2x", title: "Weisheit suchen",
    description: "Fuehre diese Woche 2 Coach-Sitzungen.",
    category: "coach", targetCount: 2, activityType: "coach_session",
    rewardPoints: 75, premiumOnly: false },
  { id: "routine_5x", title: "Rhythmus finden",
    description: "Schliesse diese Woche 5x deine Tagesroutine ab.",
    category: "routine", targetCount: 5, activityType: "tagesroutine_abgeschlossen",
    rewardPoints: 100, premiumOnly: false },
  { id: "community_3x", title: "Gemeinschaft pflegen",
    description: "Teile diese Woche 3 Beitraege in der Community.",
    category: "community", targetCount: 3, activityType: "community_beitrag",
    rewardPoints: 50, premiumOnly: false },

  // Premium-Challenges (3/Woche)
  { id: "meditation_5x", title: "Stille Woche",
    description: "Hoere diese Woche 5 Meditations-Sessions.",
    category: "meditation", targetCount: 5, activityType: "meditation_session",
    rewardPoints: 100, premiumOnly: true },
  { id: "rezepte_3x", title: "Ayurvedische Kueche",
    description: "Koche diese Woche 3 personalisierte Rezepte.",
    category: "ernaehrung", targetCount: 3, activityType: "rezept_gekocht",
    rewardPoints: 100, premiumOnly: true },
  { id: "warmes_fruehstueck", title: "Warmes Fruehstueck",
    description: "Starte diese Woche 5x mit einem warmen Fruehstueck.",
    category: "ernaehrung", targetCount: 5, activityType: "warmes_fruehstueck",
    rewardPoints: 100, premiumOnly: true, season: "winter" },
  { id: "frueh_aufstehen", title: "Brahma Muhurta",
    description: "Stehe diese Woche 4x vor 6:30 Uhr auf.",
    category: "routine", targetCount: 4, activityType: "frueh_aufstehen",
    rewardPoints: 100, premiumOnly: true },
  { id: "voice_chat_3x", title: "Lebendiges Gespraech",
    description: "Nutze diese Woche 3x den Sprach-Coach.",
    category: "coach", targetCount: 3, activityType: "voice_chat_genutzt",
    rewardPoints: 75, premiumOnly: true },
  { id: "abend_routine_7x", title: "Goldener Schlaf",
    description: "Schliesse diese Woche jeden Abend deine Abendroutine ab.",
    category: "routine", targetCount: 7, activityType: "abend_routine",
    rewardPoints: 150, premiumOnly: true },
];

// AYU Score Faktoren
export interface AyuScoreFactor {
  id: string;
  name: string;
  description: string;
  maxWeight: number;
  premiumOnly: boolean;
}

export const AYU_SCORE_FACTORS: AyuScoreFactor[] = [
  { id: "routine", name: "Tagesroutine", description: "Wie konsequent folgst du deiner Dinacharya?", maxWeight: 33, premiumOnly: false },
  { id: "stimmung", name: "Stimmung", description: "Wie ist dein emotionales Gleichgewicht?", maxWeight: 34, premiumOnly: false },
  { id: "streak", name: "Kontinuitaet", description: "Wie bestaendig bist du in deiner Praxis?", maxWeight: 33, premiumOnly: false },
  { id: "schlaf", name: "Schlaf", description: "Wie erholsam ist dein Schlaf?", maxWeight: 17, premiumOnly: true },
  { id: "ernaehrung", name: "Ernaehrung", description: "Wie phaenotyp-gerecht ernaehrst du dich?", maxWeight: 17, premiumOnly: true },
  { id: "bewegung", name: "Bewegung", description: "Wie aktiv bist du?", maxWeight: 16, premiumOnly: true },
];

// Streak Freeze Konfiguration
export const STREAK_FREEZE_CONFIG = {
  basisFreezesPerMonth: 1,
  premiumFreezesPerMonth: 3,
  softResetPenalty: 3,
  minimumStreakAfterReset: 0,
} as const;

// Hilfsfunktionen
export function calculateLevel(totalPoints: number, isPremium: boolean): AyuLevel {
  const available = isPremium ? AYU_LEVELS : AYU_LEVELS.filter((l) => !l.premiumOnly);
  let current = available[0];
  for (const level of available) {
    if (totalPoints >= level.requiredPoints) current = level;
    else break;
  }
  return current;
}

export function calculateLevelProgress(totalPoints: number, isPremium: boolean): number {
  const current = calculateLevel(totalPoints, isPremium);
  const available = isPremium ? AYU_LEVELS : AYU_LEVELS.filter((l) => !l.premiumOnly);
  const idx = available.findIndex((l) => l.id === current.id);
  const next = available[idx + 1];
  if (!next) return 100;
  const inLevel = totalPoints - current.requiredPoints;
  const needed = next.requiredPoints - current.requiredPoints;
  return Math.min(100, Math.round((inLevel / needed) * 100));
}

export function getAvailableBadges(isPremium: boolean): Badge[] {
  return isPremium ? BADGES : BADGES.filter((b) => !b.premiumOnly);
}

export function getAvailableChallenges(isPremium: boolean): ChallengeTemplate[] {
  return isPremium ? CHALLENGE_TEMPLATES : CHALLENGE_TEMPLATES.filter((c) => !c.premiumOnly);
}

export function getMonthlyFreezes(isPremium: boolean): number {
  return isPremium ? STREAK_FREEZE_CONFIG.premiumFreezesPerMonth : STREAK_FREEZE_CONFIG.basisFreezesPerMonth;
}
